import { Fragment } from "react";
import { QueryStatus, useQuery } from "@tanstack/react-query";
import { Cell, Pie, PieChart } from "recharts";

import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { Skeleton } from "@/components/ui/skeleton";
import { LockedContent } from "@/components/layout/locked-content";

import { message } from "@/lib/utils";
import type { ConsumerResearchOverviewChannelMixData } from "@/schema/brand-os/ai-consumer-research";
import { ConsumerResearchApiFactory } from "@/apis/brand-os/consumer-research";

function ChannelMixComponent({ status, data }: { status: QueryStatus; data?: ConsumerResearchOverviewChannelMixData }) {
  switch (status) {
    case "pending":
      return <ChannelMixSkeleton />;
    case "error":
      return <LockedContent message={message} className="h-full" />;
    case "success":
      return data ? <ChannelMixContent {...data} /> : <LockedContent message={message} className="h-full" />;
  }
}

export function ChannelMix() {
  const { data, status } = useQuery(ConsumerResearchApiFactory.Queries.OverviewChannelMix());

  return (
    <Card className="flex flex-col overflow-hidden">
      <CardHeader className="flex flex-row items-center gap-2">
        <CardTitle className="text-base font-semibold">Channel Mix</CardTitle>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col">
        <ChannelMixComponent status={status} data={data} />
      </CardContent>
    </Card>
  );
}

function ChannelMixContent(data: ConsumerResearchOverviewChannelMixData) {
  const channels = data?.channels ?? [];

  const pieData = channels.map((entry, i) => ({
    name: entry.channel,
    value: entry.percentage,
    mentionCount: entry.mention_count,
    fill: `var(--chart-${(i % 5) + 1})`,
  }));

  const chartConfig: ChartConfig = {};
  channels.forEach((entry, i) => {
    chartConfig[entry.channel] = {
      label: entry.channel,
      color: `var(--chart-${(i % 5) + 1})`,
    };
  });

  return (
    <Fragment>
      <ChartContainer config={chartConfig} className="w-full max-h-72 m-auto">
        <PieChart accessibilityLayer>
          <ChartTooltip cursor={false} content={<ChartTooltipContent className="font-semibold" />} />
          <Pie data={pieData} dataKey="value" nameKey="name" innerRadius="60%" outerRadius="85%" label={false} strokeWidth={2} stroke="hsl(var(--background))">
            {pieData.map((entry) => (
              <Cell key={entry.name} fill={entry.fill} />
            ))}
          </Pie>
        </PieChart>
      </ChartContainer>
      <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 max-w-sm mx-auto">
        {pieData.map((item) => (
          <div key={item.name} className="flex items-center gap-2">
            <span className="size-2.5 rounded-full" style={{ backgroundColor: item.fill }} aria-hidden />
            <span className="text-sm text-foreground capitalize">{item.name}</span>
          </div>
        ))}
      </div>
    </Fragment>
  );
}

function ChannelMixSkeleton() {
  return (
    <Card className="flex flex-col overflow-hidden">
      <CardHeader className="flex flex-row items-center gap-2">
        <CardTitle className="text-base font-semibold">
          <Skeleton className="h-6 w-28 rounded-md" />
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 flex-1 flex flex-col">
        <div className="relative mx-auto aspect-square w-full max-h-52">
          <Skeleton className="absolute inset-0 rounded-full" />
          <div className="absolute inset-[24%] rounded-full bg-background" />
        </div>
      </CardContent>
      <CardFooter className="flex flex-wrap justify-center gap-x-6 gap-y-2">
        {Array.from({ length: 5 }, (_, index) => (
          <div key={index} className="flex items-center gap-2">
            <Skeleton className="size-2.5 rounded-full" />
            <Skeleton className="h-4 w-16 rounded-md" />
          </div>
        ))}
      </CardFooter>
    </Card>
  );
}
